import React from 'react';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { Container, Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { Text, Typography } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { PriceWithFees, usePricingData, BUYABLE_TYPE } from '@fiverr-private/price_upfront_components';
import SellerTranslationBadgeWrapper from '../../SellerTranslationBadgeWrapper';
import { useShowPromotionDiscountedPriceBIEvent } from '../hooks/useShowPromotionDiscountedPriceBIEvent';
import PriceTooltipIcon from '../PriceTooltipIcon';
import { getPriceDisplayTranslationKey } from '../../../../utils/priceUpfront';
import { HeaderProps } from './types';
import './style.scss';

const HeaderDefault: React.FC<HeaderProps> = ({
  title,
  showPrice,
  description,
  showPriceTooltip,
  discountedPriceSection,
  currentPrice,
  currentPriceWithPromotionDiscount,
  currency,
}) => {
  useShowPromotionDiscountedPriceBIEvent(!!discountedPriceSection);
  const { priceDisplayStatus = 'none', isInCheetahsMobileGigPage } = useGigPageContext();
  const titleMarginBottom = discountedPriceSection ? '6' : '0';

  const { isPriceUpfrontEnabled } = usePricingData();
  const netPrice = currentPriceWithPromotionDiscount ?? currentPrice;

  return (
    <Container
      as="header"
      className="header-default"
      marginBottom={isInCheetahsMobileGigPage ? { default: '8', sm: '0' } : undefined}
    >
      <Box
        direction={{ default: 'row', sm: 'column' }}
        minHeight="48px"
        as="h3"
        alignItems={{ default: 'center', sm: 'flexStart' }}
        marginBottom={isInCheetahsMobileGigPage ? { default: '8', sm: titleMarginBottom } : titleMarginBottom}
      >
        {title && (
          <Typography
            className="package-header-title"
            color={isInCheetahsMobileGigPage ? 'grey_1100' : undefined}
            marginBottom={isInCheetahsMobileGigPage ? { default: '0', sm: '2' } : '2'}
            flex="1"
            size={isInCheetahsMobileGigPage ? { default: 'b_xl', sm: 'b_md' } : 'b_md'}
            as="b"
            fontWeight="bold"
          >
            <SellerTranslationBadgeWrapper translationKey="packages">{title}</SellerTranslationBadgeWrapper>
          </Typography>
        )}
        {showPrice && (
          <Container>
            <Stack as="span" gap="0" alignItems="center">
              <Container className="package-header-price">
                <PriceWithFees
                  netPrice={netPrice}
                  currency={currency}
                  buyableType={BUYABLE_TYPE.GIG}
                  typographyProps={{ size: priceDisplayStatus === 'both' ? 'b_md' : 'b_xl' }}
                />
                {priceDisplayStatus !== 'none' && (
                  <Text
                    paddingLeft="1.5"
                    color="bodySecondary"
                    as="span"
                    size={priceDisplayStatus === 'both' ? 'b_md' : 'b_xl'}
                  >
                    <I18n k={getPriceDisplayTranslationKey(priceDisplayStatus)} />
                  </Text>
                )}
              </Container>
              {showPriceTooltip && !isPriceUpfrontEnabled && <PriceTooltipIcon />}
            </Stack>
            {discountedPriceSection && <Typography marginTop="1">{discountedPriceSection}</Typography>}
          </Container>
        )}
      </Box>
      {description && (
        <Typography as="p" marginTop="7">
          {description}
        </Typography>
      )}
    </Container>
  );
};

export default HeaderDefault;
